import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { clientAccessService } from '../../services/clientAccessService';
import { userService } from '../../services/userService';
import {
  ClientUserAccess,
  clientUserDisplayName,
} from '../../types/clientAccess';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { useAuth } from '../../contexts/AuthContext';
import { isAbortError } from '../../utils/abortError';
import { UserDetailModal } from '../users/UserDetailModal';
import { clientService } from '../../services/clientService';
import { UserWithDetails } from '../../types/user';

interface ClientUsersTabProps {
  clientId: string;
  clientName: string;
  /** Called after the client's own access flag is toggled here. */
  onClientAccessChanged?: (active: boolean) => void;
  /**
   * Whether this client's users may use the application at all. Distinct
   * from the client's business status: cutting application access says
   * nothing about the fleet. When false, per-user toggles are moot.
   */
  clientAccess: boolean;
}

type Filter = 'all' | 'active' | 'revoked';

/**
 * Visual weight per role. ClientAdmin — the customer's own administrator, who
 * manages the other technicians — reads as the emphasised one; ordinary
 * technicians stay neutral so the hierarchy is visible without reading.
 */
const roleTone = (role: string): string => {
  switch (role) {
    case 'SuperAdmin':
    case 'Admin':
      return 'role-badge is-anep';
    case 'ClientAdmin':
    case 'Operator':
      return 'role-badge is-clientadmin';
    default:
      return 'role-badge is-standard';
  }
};

export const ClientUsersTab: React.FC<ClientUsersTabProps> = ({
  clientId,
  clientName,
  clientAccess,
  onClientAccessChanged,
}) => {
  const { formatDate, t } = useI18n();
  const { isSuperAdmin, isAdmin, canWriteUsers } = useClientAccess();
  const { user: currentUser } = useAuth();
  // Mirrors require_user_write on the API: an ANEP Admin may read the
  // directory but never change an account or its access.
  const canManageAccess = canWriteUsers;
  const showsEveryone = isSuperAdmin || isAdmin;

  const [selectedUser, setSelectedUser] = useState<UserWithDetails | null>(null);

  const [users, setUsers] = useState<ClientUserAccess[]>([]);
  const [env, setEnv] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [search, setSearch] = useState('');

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clientAccessService.fetchClientUsers(clientId, true);
      setUsers(response.users || []);
      setEnv(response.env || '');
    } catch (err) {
      if (isAbortError(err)) return;
      console.error('[CLIENT USERS] load failed', err);
      setError(t('clients.users.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [clientId, t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  /** Open the account detail view for a row. */
  const openUser = (user: ClientUserAccess) =>
    setSelectedUser({
      id: user.id,
      username: user.username,
      email: user.email,
      firstName: user.firstName,
      lastName: user.lastName,
      enabled: user.enabled,
      emailVerified: false,
      fullName: clientUserDisplayName(user),
    });

  const visibleUsers = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users.filter(user => {
      // A customer's admin manages their technicians, not themselves: seeing
      // your own row next to a switch that would lock you out is a trap.
      // ANEP staff keep seeing everyone, including themselves.
      if (!showsEveryone && user.id === currentUser?.id) return false;
      if (filter === 'active' && !user.enabled) return false;
      if (filter === 'revoked' && user.enabled) return false;
      if (!needle) return true;
      return (
        user.username.toLowerCase().includes(needle) ||
        (user.email || '').toLowerCase().includes(needle) ||
        clientUserDisplayName(user).toLowerCase().includes(needle)
      );
    });
  }, [users, filter, search, showsEveryone, currentUser?.id]);

  const activeCount = users.filter(u => u.enabled).length;

  if (loading) {
    return (
      <div className="client-users-tab" data-testid="client-users-loading">
        <div className="loading-state">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="client-users-tab" data-testid="client-users-tab">
      <div className="client-users-header">
        <div className="client-users-summary">
          <strong>{activeCount}</strong> / {users.length}{' '}
          {t('clients.users.withAccess')}
          {env && (
            <span className="env-badge" title={t('clients.users.envHint')}>
              {env}
            </span>
          )}
        </div>
        <div className="client-users-controls">
          <input
            type="search"
            className="client-users-search"
            data-testid="client-users-search"
            placeholder={t('clients.users.searchPlaceholder')}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select
            className="client-users-filter"
            data-testid="client-users-filter"
            value={filter}
            onChange={e => setFilter(e.target.value as Filter)}
          >
            <option value="all">{t('clients.users.filterAll')}</option>
            <option value="active">{t('clients.users.filterActive')}</option>
            <option value="revoked">{t('clients.users.filterRevoked')}</option>
          </select>
          <button
            className="refresh-button"
            data-testid="client-users-refresh"
            onClick={fetchUsers}
          >
            {t('common.refresh')}
          </button>
        </div>
      </div>

      {!clientAccess && (
        <div className="client-users-notice warning" data-testid="client-inactive-notice">
          {t('clients.users.clientInactiveNotice', { client: clientName })}
        </div>
      )}

      {error && (
        <div className="client-users-notice error" data-testid="client-users-error">
          {error}
        </div>
      )}

      {visibleUsers.length === 0 ? (
        <div className="empty-state" data-testid="client-users-empty">
          {users.length === 0
            ? t('clients.users.emptyNoUsers')
            : t('clients.users.emptyNoMatch')}
        </div>
      ) : (
        <table className="client-users-table" data-testid="client-users-table">
          <thead>
            <tr>
              <th>{t('users.fullName')}</th>
              <th>{t('users.username')}</th>
              <th>{t('users.email')}</th>
              <th>{t('users.roles')}</th>
              <th>{t('clients.users.statusColumn')}</th>
              <th>{t('clients.created')}</th>
            </tr>
          </thead>
          <tbody>
            {visibleUsers.map(user => (
                <tr
                  key={user.id}
                  className={[
                    'clickable-row',
                    user.enabled ? '' : 'row-revoked',
                    user.roles.some(r => r === 'ClientAdmin' || r === 'Operator')
                      ? 'row-clientadmin'
                      : '',
                  ].filter(Boolean).join(' ')}
                  data-testid={`client-user-row-${user.username}`}
                  onClick={() => openUser(user)}
                >
                  <td>
                    <div className="user-cell">
                      <button
                        type="button"
                        className="user-name-link"
                        data-testid={`open-user-${user.username}`}
                        onClick={e => {
                          // The row already handles the click; without this the
                          // handler would run twice.
                          e.stopPropagation();
                          openUser(user);
                        }}
                      >
                        {clientUserDisplayName(user)}
                      </button>

                      {user.legacyAccess && (
                        <span
                          className="status-badge legacy"
                          title={t('clients.users.legacyHint')}
                        >
                          {t('clients.users.legacyBadge')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="user-username">{user.username}</td>
                  <td>{user.email || '-'}</td>
                  <td>
                    {user.roles.length > 0 ? (
                      <div className="role-badges">
                        {user.roles.map(role => (
                          <span key={role} className={roleTone(role)}>
                            {role}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="muted">{t('clients.users.noRole')}</span>
                    )}
                  </td>
                  <td>
                    {/* Read-only here: suspending happens in the account
                        detail view, so there is exactly one switch. */}
                    <span className={`status-badge ${user.enabled ? 'active' : 'inactive'}`}>
                      {user.enabled
                        ? t('clients.users.statusActive')
                        : t('clients.users.statusSuspended')}
                    </span>
                  </td>
                  <td>
                    {user.createdTimestamp
                      ? formatDate(new Date(user.createdTimestamp))
                      : '-'}
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      )}

      <UserDetailModal
        user={selectedUser}
        isOpen={selectedUser !== null}
        onClose={() => setSelectedUser(null)}
        onUserUpdated={() => {
          setSelectedUser(null);
          fetchUsers();
        }}
      />
    </div>
  );
};
